/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Calendar reschedule handler for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsRescheduleHandler
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { rescheduleAppointment } from "../../logic/appointments/appointmentReschedule.js";
import { applyRescheduledAppointmentInCollections } from "../../logic/appointments/appointmentStateUpdates.js";
import { scheduleSuccessMessageHide } from "./viewAppointmentsSuccessMessage.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers
 * @returns {{ handleAppointmentReschedule: Function }}
 */
const useViewAppointmentsRescheduleHandler = (ctx, fetching) => {
  const {
    navigate,
    eventData,
    currentPage,
    setAppointments,
    setFilteredRequests,
    setShowRescheduleSuccessMessage,
    setIsRescheduleSlidingUp,
  } = ctx;

  const { fetchAppointmentsPage, fetchCalendarAppointments } = fetching;

  const handleAppointmentReschedule = useCallback(
    async (rescheduleData) => {
      if (!rescheduleData?.appointment) {
        logger.error("No reschedule data available");
        return;
      }

      const { appointment, newDate, newTime } = rescheduleData;

      try {
        const result = await rescheduleAppointment({
          navigate,
          appointment,
          eventData,
          newDate,
          newTime,
        });

        if (result.skipped) {
          if (result.reason === "missing_times") {
            logger.warn(
              "Appointment does not have assigned start/end times yet. Cannot reschedule.",
            );
          }
          return;
        }

        applyRescheduledAppointmentInCollections(
          appointment._id,
          result.schedule,
          { setAppointments, setFilteredRequests },
        );

        await Promise.all([
          fetchAppointmentsPage(currentPage),
          fetchCalendarAppointments(),
        ]);

        setShowRescheduleSuccessMessage(true);
        scheduleSuccessMessageHide({
          setSlidingUp: setIsRescheduleSlidingUp,
          setVisible: setShowRescheduleSuccessMessage,
        });
      } catch (error) {
        logger.error("Error rescheduling appointment:", {
          err: sanitizeError(error),
        });
      }
    },
    [
      navigate,
      eventData,
      setAppointments,
      setFilteredRequests,
      fetchAppointmentsPage,
      currentPage,
      fetchCalendarAppointments,
      setShowRescheduleSuccessMessage,
      setIsRescheduleSlidingUp,
    ],
  );

  return { handleAppointmentReschedule };
};

export default useViewAppointmentsRescheduleHandler;
